import { arrayHasItems, arrayItemCount, flatten } from '@art-suite/art-core-ts';
import { ScanPart, ScanValue, ScanValues } from '../ScanRoot';
import { isRequiredValue, ScanTemplateValue } from '../ScanTemplateRoot';
import { ScanGroupOptimized, ScanLineItemOptimized, ScanOptionOptimized, ScanPartOptimized, ScanRootOptimized, ScanStatus, ScanTaskOptimized } from '.';

export type RequiredPart = {
  valueName?: string;
  lineItemId?: string;
  optionId?: string;
  taskId?: string;
  groupId?: string;
  note?: string;
};

const EMPTY_VALUES: ScanValues = {};

const isPositiveNumber = (value: number | null | undefined): boolean => value != null && value > 0;

// **************************************************************
// COMMON PART STATUS
// **************************************************************
export const getScanPartEdited = (scanPart: ScanPart): boolean =>
  getValuesEdited(scanPart.values)
  || (scanPart.updatedAt && scanPart.createdAt && scanPart.updatedAt !== scanPart.createdAt)
  || scanPart.notApplicable
  || arrayHasItems(scanPart.photos);

// **************************************************************
// VALUES STATUS
// **************************************************************
export const getValueEdited = (value: ScanValue): boolean => value.setValue !== undefined;
export const getValueCompleted = (value: ScanValue | null | undefined, templateValue: ScanTemplateValue | undefined) =>
  value?.setValue !== undefined || value?.calculatedValue !== undefined || (templateValue && !isRequiredValue(templateValue));

export const getValuesEdited = (values: ScanValues = EMPTY_VALUES): boolean => Object.values(values).some(v => getValueEdited(v));
export const getValuesCompleted = (scanPart: ScanPartOptimized): boolean =>
  Object.keys(scanPart.values).every(name => getValueCompleted(scanPart.values[name], scanPart.template.values?.[name]));

// **************************************************************
// Scan Part STATUS
// **************************************************************
export const getScanPartHasMinimumPhotos = (scanPart: ScanPartOptimized): boolean =>
  scanPart.template.minimumPhotosRequired === null || arrayItemCount(scanPart.photos) >= (scanPart.template.minimumPhotosRequired ?? 0);
export const getScanPartCompleted = (scanPart: ScanPartOptimized): boolean => getValuesCompleted(scanPart) && getScanPartHasMinimumPhotos(scanPart);

// if a ScanPart has anything that will impact the project: labor cost, material cost, or photos, it is required to be completed (or marked not applicable)
export const getScanPartIsDynamicallyRequired = (task: ScanPartOptimized): boolean =>
  task.template.required // task is statically required
  || arrayHasItems(task.photos) // task has photos
  || isPositiveNumber(task.optimized.reducedValues.laborCost) // task has > 0 labor cost
  || isPositiveNumber(task.optimized.reducedValues.materialCost); // task has > 0 material cost

export const getScanPartMustBeCompleted = (scanPart: ScanPartOptimized): boolean => !scanPart.notApplicable && getScanPartIsDynamicallyRequired(scanPart);

export const getScanPartStarted = (scanPart: ScanPartOptimized): boolean =>
  (scanPart.createdAt && scanPart.updatedAt && scanPart.updatedAt !== scanPart.createdAt) || false;

// **************************************************************
// LINE ITEM STATUS
// **************************************************************
export const getScanLineItemSkuIdCompleted = (lineItem: ScanLineItemOptimized): boolean =>
  arrayHasItems(lineItem.template.skuIdOptions) ? lineItem.skuId != null : true;
export const getLineItemCompleted = (lineItem: ScanLineItemOptimized): boolean =>
  getScanPartStarted(lineItem) && (!getScanPartMustBeCompleted(lineItem) || (getScanPartCompleted(lineItem) && getScanLineItemSkuIdCompleted(lineItem)));
export const getLineItemEdited = (lineItem: ScanLineItemOptimized): boolean => getScanPartEdited(lineItem);
export const getLineItemStatus = (lineItem: ScanLineItemOptimized): ScanStatus =>
  getLineItemCompleted(lineItem) ? 'completed' : getLineItemEdited(lineItem) ? 'inProgress' : 'notStarted';
export const getAllLineItemsCompleted = (lineItems?: ScanLineItemOptimized[]) => !lineItems ? true : lineItems.every(getLineItemCompleted);

// **************************************************************
// OPTION STATUS - note, all Options are considered statically required since they only exist if manually added by the user; thus if they are added, they should be completed
// **************************************************************
export const getOptionCompleted = (option: ScanOptionOptimized): boolean =>
  getScanPartStarted(option) && (option.notApplicable || (getScanPartCompleted(option) && getAllLineItemsCompleted(option.lineItems)));
export const getOptionEdited = (option: ScanOptionOptimized): boolean =>
  getScanPartEdited(option) || option.lineItems?.some(li => getLineItemEdited(li)) || false;
export const getOptionStatus = (option: ScanOptionOptimized): ScanStatus =>
  getOptionCompleted(option) ? 'completed' : getOptionEdited(option) ? 'inProgress' : 'notStarted';
export const getCompletedLineItems = (option: ScanOptionOptimized) => option.lineItems?.filter(getLineItemCompleted) ?? [];
export const getAllOptionsCompleted = (options?: ScanOptionOptimized[]) => !options ? true : options.every(getOptionCompleted);

// **************************************************************
// TASK STATUS
// **************************************************************
// if a task has anything that will impact the project: labor cost, material cost, photos, or resolution options, it is required to be completed (or marked not applicable)
export const getTaskIsDynamicallyRequired = (task: ScanTaskOptimized): boolean =>
  getScanPartIsDynamicallyRequired(task)
  || arrayHasItems(task.options); // task has resolution options selected

/**
 * Tasks with options in the template must have at least one option selected in the Scan - or it must be marked not applicable
 * @param task
 * @returns
 */
export const getTaskCompleted = (task: ScanTaskOptimized): boolean =>
  getScanPartStarted(task) && (
    task.notApplicable || !getTaskIsDynamicallyRequired(task)
      ? true
      : getScanPartCompleted(task)
          && arrayHasItems(task.options)
      ? getAllOptionsCompleted(task.options) // all selected options must be completed
      : task.template.optionTemplates?.length === 0
  ); // if no options in template, task is completed, otherwise, at least one option must be selected and completed;

export const getTaskEdited = (task: ScanTaskOptimized): boolean => getScanPartEdited(task) || task.options?.some(getOptionEdited) || false;
export const getTaskStatus = (task: ScanTaskOptimized): ScanStatus => getTaskCompleted(task) ? 'completed' : getTaskEdited(task) ? 'inProgress' : 'notStarted';
export const getCompletedOptions = (task: ScanTaskOptimized) => task.options?.filter(getOptionCompleted) ?? [];
export const getAllTasksCompleted = (tasks?: ScanTaskOptimized[]) => !tasks ? true : tasks.every(getTaskCompleted);

export const getNumTasksRequired = (group: ScanGroupOptimized) => group.tasks?.filter(getTaskIsDynamicallyRequired).length ?? 0;
export const getNumTasksCompleted = (group: ScanGroupOptimized) => group.tasks?.filter(getTaskCompleted).length ?? 0;
export const getNumRequiredTasksCompleted = (group: ScanGroupOptimized) =>
  group.tasks?.filter(task => getTaskIsDynamicallyRequired(task) && getTaskCompleted(task)).length ?? 0;

// **************************************************************
// GROUP STATUS
// **************************************************************
export const getGroupCompleted = (group: ScanGroupOptimized): boolean =>
  getScanPartStarted(group) && (!getScanPartMustBeCompleted(group) || (getScanPartCompleted(group) && getAllTasksCompleted(group.tasks)));
export const getGroupEdited = (group: ScanGroupOptimized): boolean => getScanPartEdited(group) || group.tasks?.some(t => getTaskEdited(t)) || false;
export const getGroupStatus = (group: ScanGroupOptimized): ScanStatus =>
  getGroupCompleted(group) ? 'completed' : getGroupEdited(group) ? 'inProgress' : 'notStarted';
export const getCompletedTasks = (group: ScanGroupOptimized) => group.tasks?.filter(getTaskCompleted) ?? [];
export const getCompletedTaskCount = (group: ScanGroupOptimized) => getCompletedTasks(group).length;
export const getAllGroupsCompleted = (groups?: ScanGroupOptimized[]) => !groups ? true : groups.every(getGroupCompleted);

// **************************************************************
// SCAN STATUS
// **************************************************************
export const getScanCompleted = (scan: ScanRootOptimized): boolean =>
  getScanPartStarted(scan) && getScanPartCompleted(scan) && getAllGroupsCompleted(scan.groups);
export const getScanEdited = (scan: ScanRootOptimized): boolean => getScanPartEdited(scan) || scan.groups?.some(g => getGroupEdited(g)) || false;
export const getScanStatus = (scan: ScanRootOptimized): ScanStatus => getScanCompleted(scan) ? 'completed' : getScanEdited(scan) ? 'inProgress' : 'notStarted';

export const getScanPartStatus = (scanPart: ScanPartOptimized): ScanStatus => {
  switch (scanPart.scanPartType) {
    case 'scan':
      return getScanStatus(scanPart as ScanRootOptimized);
    case 'group':
      return getGroupStatus(scanPart as ScanGroupOptimized);
    case 'task':
      return getTaskStatus(scanPart as ScanTaskOptimized);
    case 'option':
      return getOptionStatus(scanPart as ScanOptionOptimized);
    case 'lineItem':
      return getLineItemStatus(scanPart as ScanLineItemOptimized);
    default:
      throw new Error(`Invalid scan part type: ${scanPart.scanPartType}`);
  }
};
// **************************************************************
// GET ALL MISSING REQUIRED THINGS (e.g. values or tasks needing options)
// **************************************************************

export const getAllMissingRequiredValues = (scanPart: ScanPartOptimized): RequiredPart[] =>
  scanPart.template.values
    ? Object.keys(scanPart.template.values).filter(key => !getValueCompleted(scanPart.values[key], scanPart.template.values![key])).map(v => ({ valueName: v }))
    : [];

export const getMissingPhotos = (scanPart: ScanPartOptimized): RequiredPart[] =>
  scanPart.template.minimumPhotosRequired && arrayItemCount(scanPart.photos) < (scanPart.template.minimumPhotosRequired ?? 0)
    ? [{ note: `Missing photos. Have: ${arrayItemCount(scanPart.photos)}/${scanPart.template.minimumPhotosRequired}` }]
    : [];

export const getAllScanPartMissingRequiredThings = (scanPart: ScanPartOptimized): RequiredPart[] =>
  flatten([
    getAllMissingRequiredValues(scanPart),
    getMissingPhotos(scanPart),
  ]);

export const getAllLineItemMissingRequiredThings = (lineItem: ScanLineItemOptimized): RequiredPart[] =>
  lineItem.notApplicable
    ? []
    : getAllScanPartMissingRequiredThings(lineItem).map(v => ({ lineItemId: lineItem.id }));

export const getAllOptionMissingRequiredThings = (option: ScanOptionOptimized): RequiredPart[] =>
  option.notApplicable
    ? []
    : flatten([
      getAllScanPartMissingRequiredThings(option),
      option.lineItems?.map(getAllLineItemMissingRequiredThings) || [],
    ]).map(v => ({ ...v, optionId: option.id }));

export const getAllTaskMissingRequiredThings = (task: ScanTaskOptimized): RequiredPart[] =>
  task.notApplicable
    ? []
    : flatten([
      getAllScanPartMissingRequiredThings(task),
      !arrayHasItems(task.options) && arrayHasItems(task.template.optionTemplates) ? [{ taskId: task.id, note: 'No options selected' }] : [],
      task.options?.map(getAllOptionMissingRequiredThings) || [],
    ]).map(v => ({ ...v, taskId: task.id }));

export const getAllGroupMissingRequiredThings = (group: ScanGroupOptimized): RequiredPart[] =>
  group.notApplicable
    ? []
    : flatten([
      getAllScanPartMissingRequiredThings(group),
      group.tasks?.map(getAllTaskMissingRequiredThings) || [],
    ]).map(v => ({ ...v, groupId: group.id }));

export const getAllScanMissingRequiredThings = (scan: ScanRootOptimized): RequiredPart[] =>
  flatten([
    getAllScanPartMissingRequiredThings(scan),
    scan.groups?.map(getAllGroupMissingRequiredThings) || [],
  ]);
